import type { StickyFilterSlice } from '../StickyFilterSlice';
import type { StoreAdapter } from '../StoreAdapter';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { DefaultError } from '@tanstack/query-core';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import type { Order } from '@thinkalpha/trading-ws-client/contracts/order.js';
import { optional } from 'inversify';
import { createStructuredSelector } from 'reselect';
import { inject, injectable, ReactiveInjectable, reacts } from 'src/features/ioc';
import { instrumentToString, parseInstrument } from 'src/lib/instrumentUtils';
import { getOrders } from 'src/lib/orders';
import type { BlotterWidgetModel } from 'src/models/BlotterWidgetModel';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { BlotterGridDefaults } from 'src/routes/widgets/BlotterWidget/types';
import type { TradingSocketConnectionStatus, TradingSocketService } from 'src/services/TradingSocketService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { RootState } from 'src/store';
import { removeContainer, setMaximizedContainer } from 'src/store/actions/container';
import { removeTab, updateActiveTab, updateTabName } from 'src/store/actions/tab';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import {
    updateOrderBlotterInstrument,
    userDoubleClickedOrderFromBlotter,
} from 'src/store/actions/widgets/orderBlotter';
import { userDoubleClickedSymbolFromTable } from 'src/store/actions/widgets/results';
import { getMaximizedContainerId } from 'src/store/selectors';
import type { OrderBlotterWidgetViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

const makeSelectWidgetData = (tabId: string) =>
    createStructuredSelector({
        tab: (state: RootState) => state.tab.byId[tabId],
    });

@injectable()
export class BlotterWidgetModelImpl extends ReactiveInjectable implements BlotterWidgetModel {
    #instrument!: Signal<Instrument>;
    #accountSelectionValid = signal(true);
    #accountIds!: Signal<string[] | null>;
    #statusFilterSignal!: Signal<OrderBlotterWidgetViewModel['statusFilter']>;
    #todayOnly = signal(true);
    #formattingSettings!: Signal<OrderBlotterWidgetViewModel['formattingSettings'] | null>;
    #ordersQueryOptions!: ReadonlySignal<UseQueryOptions<Order[]>>;
    #ordersQueryKey!: ReadonlySignal<readonly unknown[]>;
    #ordersQueryModel!: QueryObserverModel<Order[]>;
    #orders!: ReadonlySignal<Order[]>;
    #ordersLoading!: ReadonlySignal<boolean>;
    #connectionStatus!: ReadonlySignal<TradingSocketConnectionStatus>;
    #tradingSocketService: TradingSocketService;
    #channel!: Signal<string | null>;
    #log: ReactBindings['Logger'];
    #isAccountSelectionValid: Signal<boolean> = signal(false);
    #stickyFilterSlice: StickyFilterSlice;
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    // TODO: Blotter widget uses connection state from TradingSocketService, not TableModel
    //  Figure out what to do with these as TradingSocketService does not expose this data
    #tableFailureCount: ReadonlySignal<number> = signal(0);
    #tableProgress: ReadonlySignal<number | undefined> = signal(undefined);

    // TODO: Should this go on widget definition and not widget instance?
    headerElements: WidgetHeaderElement[] = [
        ...COMMON_HEADER_ELEMENTS,
        { type: 'accounts', priority: 5, position: 60, selectType: 'multi', allAccountsLabel: 'my-activity' },
        { type: 'blotterStatusFilter', position: 70, priority: 6 },
        { type: 'stickyFilter', position: 40, priority: 70 },
        { type: 'blotterTodayOnly', position: 80, priority: 60 },
        { type: 'gridSettingsDialog', position: 90, priority: 50 },
        { type: 'connectionStatus', position: 995, priority: 995, overflowPosition: 5 },
    ];

    constructor(
        @inject('QueryObserverModelFactory')
        private queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('TradingSocketService') tradingSocketService: TradingSocketService,
        @inject('Store') private store: ReactBindings['Store'],
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('IoCContainer') private readonly iocContainer: ReactBindings['IoCContainer'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#log = logger.getSubLogger({ name: 'BlotterWidgetModel' });

        this.#tradingSocketService = tradingSocketService;
        this.#stickyFilterSlice = stickyFilterSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        // Build eventBus in constructor after stickyFilterSlice is available
        this.eventBus = this.#buildEventBus();

        this.#connectionStatus = computed((): TradingSocketConnectionStatus => {
            const connected = this.#tradingSocketService.isConnected.value;
            const authenticated = this.#tradingSocketService.isAuthenticated.value;
            if (connected && authenticated) {
                return 'connected';
            }

            if (connected) {
                return 'connecting';
            }

            return 'disconnected';
        });
    }

    private get widget() {
        return this.widgetData.widget as OrderBlotterWidgetViewModel;
    }

    #removeContainerImperatively(containerId: string): void {
        void this.iocContainer
            .get('WorkspaceService')
            .removeContainer(containerId)
            .catch((error) => {
                this.#log.error({ message: 'WorkspaceService.removeContainer failed', details: error });
            });
    }

    #removeWidgetImperatively(tabId: string): void {
        void this.iocContainer
            .get('WorkspaceService')
            .removeWidget(tabId)
            .catch((error) => {
                this.#log.error({ message: 'WorkspaceService.removeWidget failed', details: error });
            });
    }

    init(tabId: string) {
        const selectWidgetData = makeSelectWidgetData(tabId);
        this.widgetData.init(tabId);
        this.#channel = signal(this.widget.channelId);
        // TODO: change these to initializing signals
        this.#instrument = signal(this.widget.instrument || parseInstrument(''));
        this.#accountIds = signal(this.widget.accountIds ?? null);
        this.#statusFilterSignal = signal(this.widget.statusFilter ?? null);
        this.#formattingSettings = signal(this.widget.formattingSettings ?? null);

        // Initialize StickyFilterSlice with a lightweight adapter
        // Blotter uses 'tickerFilter' in Redux, mapped to 'filter' for the slice
        const stickyFilterAdapter: StoreAdapter<{ filter?: string | null }> = {
            getState: () => ({
                filter: (this.widget as OrderBlotterWidgetViewModel).tickerFilter ?? null,
            }),
            persist: (partial) => {
                if ('filter' in partial) {
                    this.store.dispatch(updateWidget({ tabId, widgetUpdates: { tickerFilter: partial.filter } }));
                }
            },
            subscribe: (cb) => {
                return this.store
                    .subscribeSelector((state) => {
                        const tab = state.tab.byId[tabId];
                        if (!tab) return null;
                        return { filter: (tab.widget as OrderBlotterWidgetViewModel).tickerFilter ?? null };
                    })
                    .handle((data) => {
                        if (data) cb(data);
                    });
            },
            [Symbol.dispose]() {
                // no-op — lifecycle managed by disposableStack
            },
        };
        this.#stickyFilterSlice.init(stickyFilterAdapter);
        this.disposableStack.use(this.#stickyFilterSlice);

        this.#ordersQueryOptions = computed(() => {
            const accountIds = this.#accountIds.value;
            const todayOnly = this.#todayOnly.value;

            return {
                queryKey: [
                    'blotter-orders',
                    accountIds?.length ? accountIds.join(',') : 'NO-ACCOUNT-FILTER',
                    todayOnly ? 'TODAY-ONLY' : '',
                ] as const,
                queryFn: async () => {
                    const orders = await getOrders({
                        accounts: accountIds ?? undefined,
                        todayOnly,
                        actors: accountIds?.length ? undefined : ['me'],
                    });
                    return orders;
                },
            };
        });
        this.#ordersQueryKey = computed(() => this.#ordersQueryOptions.value.queryKey);

        this.#ordersQueryModel = this.queryObserverModelFactory<Order[], DefaultError, Order[]>(
            this.#ordersQueryOptions.value,
        );
        this.disposableStack.use(this.#ordersQueryModel);

        this.disposableStack.defer(
            effect(() => {
                const options = this.#ordersQueryOptions.value;
                this.#ordersQueryModel.setQueryOptions(options);
                this.#ordersQueryModel.queryObserver.refetch();
            }),
        );

        this.#orders = computed(() => this.#ordersQueryModel.signal.value.data ?? EmptyArray);
        this.#ordersLoading = computed(() => this.#ordersQueryModel.signal.value.isLoading ?? true);

        // Listen for widget updates
        const unsubscribe = this.store
            .subscribeSelector((state) => {
                // Check if the tab still exists before trying to select it
                const tabExists = state.tab.byId[tabId] !== undefined;
                if (!tabExists) {
                    // Tab has been removed, return null to indicate no update needed
                    return null;
                }
                return selectWidgetData(state);
            })
            .handle((widgetData) => {
                // Only update if widgetData is not null (tab still exists)
                if (widgetData !== null) {
                    const widget = widgetData.tab.widget as OrderBlotterWidgetViewModel;
                    this.#instrument.value = widget.instrument || parseInstrument('');
                    this.#accountIds.value = widget.accountIds ?? null;
                    this.#statusFilterSignal.value = widget.statusFilter ?? null;
                    this.#formattingSettings.value = widget.formattingSettings ?? null;
                    this.#channel.value = widget.channelId;
                }
            });

        this.disposableStack.defer(() => {
            unsubscribe();
        });
    }

    get channel(): ReadonlySignal<string | null> {
        return this.#channel;
    }

    eventBus!: BlotterWidgetModel['eventBus'];

    #buildEventBus(): BlotterWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Events
                .addEvent('widget::channelChange', (payload) => {
                    this.widgetData.changeChannel(payload.channel);
                })
                .addEvent('widget::resetChannelData', () => {
                    this.widgetData.resetChannelData();
                })
                .addEvent('widget::instrumentChange', (payload) => {
                    this.store.dispatch(
                        updateOrderBlotterInstrument(this.widgetData.tabId, parseInstrument(payload.instrument)),
                    );
                })
                .addEvent('widget::toggleMaximized', () => {
                    const isMaximized = getMaximizedContainerId(this.store.getState()) === this.widgetData.containerId;
                    this.store.dispatch(setMaximizedContainer(isMaximized ? undefined : this.widgetData.containerId));
                })
                .addEvent('widget::delete', () => {
                    if (APP_ELECTRON) {
                        this.#removeContainerImperatively(this.widgetData.containerId);
                        return;
                    }
                    this.store.dispatch(removeContainer(this.widgetData.containerId));
                })
                .addEvent('widget::updateSelectedAccountIds', (payload) => {
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: { accountIds: payload.accountIds },
                        }),
                    );
                })
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    // Blotter uses multi-select, so convert single to array
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: { accountIds: [payload.accountId] },
                        }),
                    );
                })
                .addEvent('widget::setIsAccountSelectionValid', (payload) => {
                    this.#isAccountSelectionValid.value = payload.isValid;
                })
                .addEvent('widget::toggleBlotterTodayOnly', (payload) => {
                    this.#todayOnly.value = payload.todayOnly;
                })
                .addEvent('widget::changeOrderStatusFilter', (payload) => {
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: { statusFilter: payload.statusFilter },
                        }),
                    );
                })
                .addEvent('widget::changeTab', (payload) => {
                    this.store.dispatch(updateActiveTab(this.widgetData.containerId, payload.tabId));
                })
                .addEvent('widget::deleteTab', (payload) => {
                    const parentContainer = this.store.getState().container.byId[this.widgetData.containerId];
                    const normalizedTabsByKey = this.store.getState().tab.byId;
                    if (parentContainer.tabIds.length === 1) {
                        if (APP_ELECTRON) {
                            this.#removeContainerImperatively(parentContainer.id);
                            return;
                        }
                        this.store.dispatch(removeContainer(parentContainer.id));
                    } else {
                        if (APP_ELECTRON) {
                            this.#removeWidgetImperatively(payload.tabKey);
                            return;
                        }
                        const newActiveTabKey = parentContainer.tabIds.find((key) => key !== payload.tabKey);
                        const newActiveTabId = normalizedTabsByKey[newActiveTabKey!].id;
                        const widgetType = normalizedTabsByKey[payload.tabKey]?.widget?.type ?? 'unknown';
                        this.store.dispatch(removeTab(payload.tabKey, parentContainer.id, newActiveTabId!, widgetType));
                    }
                })
                .addEvent('widget::changeTabName', (payload) => {
                    this.store.dispatch(updateTabName(payload.tab.id, payload.name));
                })
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                })
                // Signals
                .addSignal('widget::instrument', () => this.instrumentString)
                .addSignal('widget::channel', () => this.#channel)
                .addSignal('widget::selectedAccountIds', () =>
                    computed(() => this.#accountIds.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::selectedAccountId', () =>
                    computed(() => (this.#accountIds.value && this.#accountIds.value[0]) ?? null),
                )
                .addSignal('widget::isAccountSelectionValid', () => this.#isAccountSelectionValid)
                .addSignal('widget::blotterTodayOnly', () => this.#todayOnly)
                .addSignal('widget::orderStatusFilter', () => this.#statusFilterSignal)
                .addSignal(
                    'widget::connectionStatus',
                    () =>
                        computed(() => ({
                            status: this.#connectionStatus.value,
                            failureCount: this.#tableFailureCount.value,
                            progress: this.#tableProgress.value,
                        })) satisfies ConnectionStatusSignal[keyof ConnectionStatusSignal],
                )
                .addSignal('widget::name', () => computed(() => this.widgetData.displayName))
                .addSignal('widget::isMaximized', () =>
                    computed(() => getMaximizedContainerId(this.store.getState()) === this.widgetData.containerId),
                )
                .addSignal('widget::tabId', () => computed(() => this.widgetData.tabId))
                .addSignal('widget::container', () =>
                    computed(() => this.store.getState().container.byId[this.widgetData.containerId]),
                )
                .addSignal('widget::containerActiveTab', () => computed(() => this.widgetData.tab))
                .addSignal('widget::tabsById', () => computed(() => this.store.getState().tab.byId))
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => ({ settings: this.#formattingSettings.value, defaults: BlotterGridDefaults })),
                )
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .build(),
        ).merge(this.#stickyFilterSlice.eventBus);
    }

    userSelectedSymbol(symbol: string) {
        this.store.dispatch(userDoubleClickedSymbolFromTable(this.widgetData.tabId, symbol));
    }

    userSelectedOrder(symbol?: string, accountId?: string) {
        this.store.dispatch(userDoubleClickedOrderFromBlotter(this.widgetData.tabId, symbol, accountId));
    }

    get instrument() {
        return this.#instrument;
    }

    instrumentString = computed(() => instrumentToString(this.#instrument.value));

    setInstrument(instrument: Instrument) {
        this.store.dispatch(updateOrderBlotterInstrument(this.widgetData.tabId, instrument));
    }

    get selectedAccountIds() {
        return this.#accountIds.value ?? EmptyArray;
    }

    updateWidgetState(updates: Partial<OrderBlotterWidgetViewModel>) {
        this.store.dispatch(
            updateWidget({
                tabId: this.widgetData.tabId,
                widgetUpdates: updates,
            }),
        );
    }

    get isAccountSelectionValid(): ReadonlySignal<boolean> {
        return this.#accountSelectionValid;
    }

    setAccountSelectionValidity(isValid: boolean) {
        this.#accountSelectionValid.value = isValid;
    }

    get statusFilter() {
        return this.#statusFilterSignal.value;
    }

    get todayOnly(): ReadonlySignal<boolean> {
        return this.#todayOnly;
    }

    setTodayOnly(enabled: boolean) {
        if (this.#todayOnly.value === enabled) {
            return;
        }
        this.#todayOnly.value = enabled;
    }

    get orders(): ReadonlySignal<Order[]> {
        return this.#orders;
    }

    get ordersLoading(): ReadonlySignal<boolean> {
        return this.#ordersLoading;
    }

    get ordersQueryKey(): ReadonlySignal<readonly unknown[]> {
        return this.#ordersQueryKey;
    }

    get connectionStatus(): ReadonlySignal<TradingSocketConnectionStatus> {
        return this.#connectionStatus;
    }

    get formattingSettings(): ReadonlySignal<OrderBlotterWidgetViewModel['formattingSettings'] | null> {
        return this.#formattingSettings;
    }
}
